import { useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Reader } from '@/features/reader';

/**
 * Reader page — thin wrapper around `<Reader>`. Decodes route params,
 * defends against malformed input, and wires the active-page back into the
 * URL with a `replace` (so the browser-back button still returns to the
 * gallery detail, not to every page the user paged through).
 */
export function ReaderPage() {
  const { sourceId, comicId, page } = useParams<{
    sourceId: string;
    comicId: string;
    page?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const id = comicId ? decodeURIComponent(comicId) : '';
  const initialPage = page ? Math.max(1, Number.parseInt(page, 10) || 1) : 1;
  const readUnitId = new URLSearchParams(location.search).get('unit') ?? undefined;

  const handlePageChange = useCallback(
    (next: number) => {
      if (!sourceId || !id) return;
      const params = new URLSearchParams(location.search);
      if (readUnitId) params.set('unit', readUnitId);
      const query = params.toString();
      navigate(`/source/${sourceId}/comic/${encodeURIComponent(id)}/read/${next}${query ? `?${query}` : ''}`, {
        replace: true,
        state: location.state,
      });
    },
    [id, location.search, location.state, navigate, readUnitId, sourceId],
  );

  if (!sourceId || !id) {
    return (
      <div className="flex flex-col gap-2 p-6">
        <h1 className="text-2xl font-bold">Bad reader URL</h1>
        <p className="text-sm text-muted-foreground">
          The URL is missing a source or comic id. Try again from the source page.
        </p>
        <Link to="/" className="text-sm underline">
          ← Back home
        </Link>
      </div>
    );
  }

  return (
    <Reader
      sourceId={sourceId}
      comicId={id}
      readUnitId={readUnitId}
      initialPage={initialPage}
      onPageChange={handlePageChange}
    />
  );
}
